import type { ApiObject } from "@rudderstack/analytics-js";
import type { TRACK_EVENT_PAYLOAD } from "@sinch/bus";
import { isString } from "@sinch/feature-flags";
import type { ShellUser } from "../api/types";
import { locationAtom, prevLocationAtom } from "../components/Router/state";
import { getSessionId } from "../featureFlags";
import { ENV } from "../helpers/getEnv";
import { safeRetrieveValue } from "../helpers/safeRetrieveValue";
import { globalJotaiStore } from "../jotai-store";
import {
  getDeviceType,
  getWindowDimension,
  getOSName,
  getBrowserName,
  getBrowserVersion,
} from "./deviceInformation";
import type { RudderStackClientEvent } from "./eventTypes";
import { getRudderStackAnonymousId } from "./rudderstack";

// Refers to https://confluence.sinch.com/pages/viewpage.action?pageId=290292852
export enum EVENT_TYPE {
  TEST_EVENT = "test_event",
  USER_INTERACTION = "user_interaction",
  PAGE_LOADED = "page_loaded",
  USER_LOGIN = "user_login",
  USER_LOGOUT = "user_logout",
}

export enum EVENT_NAME {
  TEST_EVENT = "test_event",
  PAGE_LOADED = "Page Loaded",
  USER_LOGIN = "User Logged In",
  USER_LOGOUT = "User Logged out",
}

const matchEventNameToEventType = (eventName: string) => {
  switch (eventName) {
    case EVENT_NAME.TEST_EVENT:
      return EVENT_TYPE.TEST_EVENT;
    case EVENT_NAME.PAGE_LOADED:
      return EVENT_TYPE.PAGE_LOADED;
    case EVENT_NAME.USER_LOGIN:
      return EVENT_TYPE.USER_LOGIN;
    case EVENT_NAME.USER_LOGOUT:
      return EVENT_TYPE.USER_LOGOUT;
    default:
      return EVENT_TYPE.USER_INTERACTION;
  }
};

export type TrackEventPayloadWithOptionalPurposes = Omit<
  TRACK_EVENT_PAYLOAD,
  "purposes"
> & {
  purposes?: TRACK_EVENT_PAYLOAD["purposes"] | undefined;
};

export const createRudderStackClientEvent = (
  payload: TrackEventPayloadWithOptionalPurposes,
  user: ShellUser
): RudderStackClientEvent => {
  const customInfo = payload.customInfo || payload.specificInfo;
  return {
    event: {
      tracing_id: payload.id,
      type: `com.sinch.onesaas.${matchEventNameToEventType(payload.name)}`,
      source: "com.sinch.onesaas.frameworks",
      timestamp: Date.now(),
      session_id: getSessionId() || "",
      name: toSnakeCase(payload.appName + "_") + payload.name,
      action: payload.action || "",
      tag_version: payload.tag || "",
      target: payload.target,
    },
    user: {
      id: user.id,
      anonymous_id: getRudderStackAnonymousId() || "",
      country:
        safeRetrieveValue(user.settings, "defaultCountryCode", isString) ?? "",
      language: user.language ?? "",
      role: user.roles || [],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    account: {
      id: user.account?.id || "",
      // TODO: fix
      tier: "",
      vendor_id: safeRetrieveValue(user.settings, "vendorId", isString) ?? "",
    },
    device: {
      type: getDeviceType(),
      viewport_size: `${getWindowDimension().width}x${
        getWindowDimension().height
      }`,
      os: getOSName(),
      browser: getBrowserName(),
      browser_version: getBrowserVersion(),
      user_agent: navigator.userAgent,
    },
    app: {
      // TODO: Fix this with entry
      name: "Onesaas",
      mfe: payload.appName,
      env: ENV,
    },
    page: {
      url: globalJotaiStore.get(locationAtom).href,
      prev_url: globalJotaiStore.get(prevLocationAtom)?.href || "",
    },
    experiment: {
      name: payload.experimentInfo?.experimentId || "",
      bucket: payload.experimentInfo?.bucketId || "",
    },
    custom: customInfo ? formatCustomProps(customInfo, payload.appName) : {},
  };
};

const toSnakeCase = (s: string) =>
  s
    // handle uppercases that already have an underscore behind
    .replace(/_([A-Z]+)/g, (x) => x.toLowerCase())
    // handle other uppercases
    .replace(/([A-Z]+)/g, (x) => "_" + x.toLowerCase())
    // handle other separators
    .replace(/[\s.-]/g, "_")
    // handle multiple underscores
    .replace(/_+/g, "_");

const formatCustomProps = (
  customProps: ApiObject,
  name: string,
  isRoot = true
): ApiObject => {
  const toReturn: ApiObject = {};

  for (const i in customProps) {
    const currentValue = customProps[i];

    if (
      typeof currentValue == "object" &&
      !Array.isArray(currentValue) &&
      currentValue !== null
    ) {
      const flatObject = formatCustomProps(currentValue, name, false);
      for (const x in flatObject) {
        toReturn[isRoot ? toSnakeCase(name + "_" + i + "_" + x) : i + "_" + x] =
          flatObject[x];
      }
    } else {
      toReturn[isRoot ? toSnakeCase(name + "_" + i) : i] = currentValue;
    }
  }
  return toReturn;
};
